import { DatePipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatLegacyMenu as MatMenu } from '@angular/material/legacy-menu';
import { InterfaceAppServiceRegister } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { EFieldsTypes } from '@app/core/enums';
import { FormFilterTableControlService } from '@app/modules/table/services/form-filter-table-control.service';
import { mbHasMatch } from '@fui/fui-helpers';
import { FuiDestroyService } from '@services/destroy.service';
import { NotifierService } from '@services/notifier.service';
import * as moment from 'moment';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { TableComponent } from '../table/table.component';
import { FormFilterFieldsComponent } from './form-filter-fields/form-filter-fields.component';
import { FormFilterFieldsService } from './form-filter-fields/form-filter-fields.service';

@Component({
    selector: 'app-form-filter',
    templateUrl: './form-filter.component.html',
    styleUrls: ['./form-filter.component.scss'],
    providers: [FormFilterFieldsService, FuiDestroyService],
})
export class FormFilterComponent implements OnInit {
    private readonly destroy$ = inject(FuiDestroyService);
    private readonly notifier = inject(NotifierService);

    readonly appService = inject(AppService);
    readonly datePipe = inject(DatePipe);
    readonly tableForm = inject(TableComponent);
    readonly formFilterFieldsService = inject(FormFilterFieldsService);
    readonly formFilter = inject(FormFilterTableControlService);

    @ViewChild('formFilterFields') formFilterFields?: FormFilterFieldsComponent;

    /** Объект меню фильтра */
    @ViewChild('menuFilter', { static: false }) private _menuFilter: MatMenu;

    @Input() formDynamic: FormGroup;

    /** Данные фильтра */
    @Input() settingData: any;

    /** Сервис взаимодействия страницы  */
    @Input() tableService: any;

    /** Выполнение фильтрации */
    @Output() setFilter = new EventEmitter<void>();

    /**
     * Выбросить событие "Настроить фильтр"
     */
    @Output() setupFilterClicked = new EventEmitter<void>();

    /**  Списки */
    registers: { [key: string]: InterfaceAppServiceRegister[] } = {};
    registersLoaded$ = new BehaviorSubject(false);

    /**
     * Метод для поиска символов в строке
     */
    protected readonly mbHasMatch = mbHasMatch;

    /**
     * Количество скрытых чипсов в фильтре
     */
    public chipsHidden: number = 0;

    /** Получить меню фильтра */
    public get menuFilter() {
        return this._menuFilter;
    }

    /**
     * Прятать кнопки действий в расширенном фильтре, если нужно
     * Kнопка "Настроить фильтр" прячется всегда, кроме случая setupFilter === true
     */
    @Input() hideButtons: { menu?: { setupFilter?: boolean; save?: boolean; map?: boolean } } | null = null;

    ngOnInit() {
        this.getRegisters();
        this.formFilterFieldsService.isInitForm$
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => this.isFormData(value));
    }

    /**
     * Получение справочников
     */
    getRegisters() {
        this.registersLoaded$.next(false);
        if (this.settingData.scopes && this.settingData.scopes.length) {
            this.appService
                .getRegisters(this.settingData.scopes)
                .toPromise()
                .then((result: any) => {
                    if (result && result.is_error == false) {
                        this.registers = result.data;
                    } else {
                        this.notifier.notify('error', result.message);
                    }
                    this.registersLoaded$.next(true);
                })
                .catch(() => {
                    this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
                    this.registersLoaded$.next(true);
                });
        } else {
            this.registersLoaded$.next(true);
        }
    }

    get isMapButtonVisible() {
        return (
            this.tableForm.settingsTable.isMap &&
            this.tableService &&
            this.tableService.saveAsLayer &&
            this.appService.accessMatrix.map_admin.add === 1
        );
    }

    /**
     * Получение значения поля
     * @param field поле
     * @param form форма
     */
    getValueItem(field: any, form: FormGroup) {
        const value = [];
        let val = [];

        if (!field.filter) {
            return '';
        }

        switch (field.filter.type) {
            case EFieldsTypes.registry:
                val = [];
                if (form.get(field.name).value.length) {
                    form.get(field.name).value.forEach((id) => {
                        const list = this.registers[field.filter.scope].find((row) => row.id == id);
                        if (list) {
                            val.push(list.name);
                        }
                    });
                }
                value.push(val.join(', '));
                break;
            case EFieldsTypes.list:
                val = [];
                if (form.get(field.name).value?.length) {
                    form.get(field.name).value.forEach((id) => {
                        const list = field.filter.values.find((row) => row.id == id);
                        if (list) {
                            val.push(list.name);
                        }
                    });
                }
                value.push(val.join(', '));
                break;
            case EFieldsTypes.technical_process:
                if (form.get(field.name).get('tp').value) {
                    value.push(form.get(field.name).get('tp').value);
                }
                if (form.get(field.name).get('regulation').value) {
                    value.push(form.get(field.name).get('regulation').value);
                }
                break;
            case EFieldsTypes.bool:
                /* Если пришло true / false */
                if (typeof form.get(field.name).value == 'boolean') {
                    value.push(
                        form.get(field.name).value
                            ? this.appService.language_dictionary.system_yes
                            : this.appService.language_dictionary.system_no,
                    );
                } else if (form.get(field.name).value == '1' || form.get(field.name).value == '0') {
                    /* Если пришло 1/0 */
                    value.push(
                        form.get(field.name).value == '1'
                            ? this.appService.language_dictionary.system_yes
                            : this.appService.language_dictionary.system_no,
                    );
                }
                break;
            case EFieldsTypes.date:
                if (form.get(field.name).get('from').value) {
                    value.push(
                        this.appService.language_dictionary.system_from +
                            ' ' +
                            this.date(form.get(field.name).get('from').value),
                    );
                }
                if (form.get(field.name).get('to').value) {
                    value.push(
                        this.appService.language_dictionary.system_to +
                            ' ' +
                            this.date(form.get(field.name).get('to').value),
                    );
                }
                break;
            case EFieldsTypes.digit:
                if (form.get(field.name).get('from').value) {
                    value.push(
                        this.appService.language_dictionary.system_from + ' ' + form.get(field.name).get('from').value,
                    );
                }
                if (form.get(field.name).get('to').value) {
                    value.push(
                        this.appService.language_dictionary.system_to + ' ' + form.get(field.name).get('to').value,
                    );
                }
                break;
            case EFieldsTypes.access:
                const access = [];
                const subjectVal = form.get(field.name).get('subject').value;
                const subjectListName = 'user_access_level_subject(' + field?.user_access_id + ')';
                if (form.get(field.name).get('subject').value && form.get(field.name).get('subject').value.length) {
                    access.push(
                        this.appService.language_dictionary.system_subject +
                            ': ' +
                            this.getRegistersTextByIds(subjectListName, subjectVal),
                    );
                }
                if (form.get(field.name).get('user').value && form.get(field.name).get('user').value.length) {
                    access.push(
                        this.appService.language_dictionary.system_user +
                            ': ' +
                            this.getRegistersTextByIds('users', form.get(field.name).get('user').value),
                    );
                }
                if (access.length) {
                    value.push(access.join(', '));
                }
                break;
            case EFieldsTypes.address:
                const address = [];
                if (form?.get(field.name)?.get('region_name')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_region +
                            ': ' +
                            form.get(field.name).get('region_name').value,
                    );
                }
                if (form?.get(field.name)?.get('district_name')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_district +
                            ': ' +
                            form.get(field.name).get('district_name').value,
                    );
                }
                if (form?.get(field.name)?.get('city_name')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_city +
                            ': ' +
                            form.get(field.name).get('city_name').value,
                    );
                }
                const street = [];
                if (form?.get(field.name)?.get('street_1')?.value) {
                    street.push(form.get(field.name).get('street_1').value);
                }
                if (form?.get(field.name)?.get('street_2')?.value) {
                    street.push(form.get(field.name).get('street_2').value);
                }
                if (form?.get(field.name)?.get('street_3')?.value) {
                    street.push(form.get(field.name).get('street_3').value);
                }
                if (street.length) {
                    address.push(this.appService.language_dictionary.address_dialog_street + ': ' + street.join(', '));
                }
                if (form?.get(field.name)?.get('building')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_building +
                            ': ' +
                            form.get(field.name).get('building').value,
                    );
                }
                if (form?.get(field.name)?.get('apartment')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_apartment +
                            ': ' +
                            form.get(field.name).get('apartment').value,
                    );
                }
                value.push(address.join(', '));
                break;
            default:
                const formVal = form.get(field.name).value;
                if (typeof formVal !== 'object') {
                    value.push(form.get(field.name).value);
                }
        }

        return value.join(' ');
    }

    /**
     * Получение списка записей по ИД
     * @param register список
     * @param ids ИД записей
     */
    getRegistersTextByIds(register: string, ids: any[]) {
        const result = [];
        this.registers[register]?.forEach((item) => {
            if (ids.indexOf(item.id) != -1) {
                result.push(item.name);
            }
        });
        return result.join(', ');
    }

    /**
     * Форматер даты
     */
    date(input: any) {
        return input && moment(input).isValid() ? this.datePipe.transform(input, 'shortDate') : '';
    }

    /**
     * Проставление флага формы
     * //TODO: не уверена что это нужно в таком виде
     */
    isFormData(flag = false) {
        Object.keys(this.formDynamic.controls).forEach((key) => {
            const form = this.formDynamic.get(key) as FormGroup;
            if (form && form.controls && Object.keys(form.controls).length) {
                Object.keys(form.controls).forEach((child) => {
                    if (typeof form.get(child)?.value === 'object' && form.get(child)?.value?.length) {
                        this.formFilter.isOpenFilter = flag;
                    } else if (form.get(child)?.value && typeof form.get(child)?.value !== 'object') {
                        this.formFilter.isOpenFilter = flag;
                    }
                });
            } else if (form.value) {
                this.formFilter.isOpenFilter = flag;
            }
        });
    }

    /**
     * Проверка пустоты поля
     */
    isFieldData(form: FormGroup | any): boolean {
        const hasValue = (val: any): boolean => {
            if (val === null) {
                return false;
            }
            if (Array.isArray(val)) {
                return val.length > 0;
            }
            if (typeof val === 'object') {
                return Object.values(val).some((v) => hasValue(v));
            }
            if (typeof val === 'string') {
                return val.trim() !== '';
            }
            return true;
        };

        if (!form) {
            return false;
        }

        if (form.controls && Object.keys(form.controls).length) {
            return Object.keys(form.controls).some((key) => hasValue(form.get(key)?.value));
        }

        return hasValue(form.value);
    }

    /**
     * Проверка пустоты блока фильтра
     * @param fields массив полей
     */
    isSubFormData(fields: any[]) {
        let isNotEmpty = false;
        fields.forEach((field) => {
            if (this.isFieldData(this.formDynamic.get(field.name))) {
                isNotEmpty = true;
            }
        });
        return isNotEmpty;
    }

    /**
     * Определяем наличие формы показа фильтра
     */
    isSubForm() {
        return this.formFilterFieldsService.entityGrid.some(
            (item) =>
                this.formFilterFieldsService.fieldGrid[item.name] &&
                this.formFilterFieldsService.fieldGrid[item.name].length > 0 &&
                this.isSubFormData(this.formFilterFieldsService.fieldGrid[item.name]),
        );
    }

    /**
     * Очистка поля формы
     * @param field поле формы
     */
    clearField(field: FormGroup | any) {
        if (field.controls && Object.keys(field.controls).length) {
            Object.keys(field.controls).forEach((key) => {
                field.get(key).setValue(null);
            });
        } else {
            field.setValue(null);
        }
        if (!this.isSubForm()) {
            this.tableForm.clearExtendFilter();
            this.formFilter.isFilterSet = false;
        }
        this.setFilter.next();
        this.formFilter.setFilterChanged();
    }

    /**
     * Обработка нажатия на поиск
     * @param showForm
     */
    public clickSearch(showForm: boolean): void {
        if (showForm) {
            this.formFilter.isOpenFilter = false;
        } else {
            this.tableForm.isSpinnerFullScreen = true;
            this.tableForm.setExtendFilter();
        }

        // Подсчёт чипсов
        this.updateChips();
        // Устанавливаем флаг, что фильтр применён
        this.formFilter.isFilterSet = true;
    }

    /** Подсчёт активных полей для чипсов */
    private updateChips(): void {
        if (!this.formDynamic) {
            return;
        }

        let count = 0;
        Object.keys(this.formDynamic.controls).forEach((key) => {
            const control = this.formDynamic.get(key);
            if (this.isFieldData(control)) {
                count++;
            }
        });
        this.chipsHidden = count;
    }

    /** Обработка нажатия на очистить */
    public clickClear(): void {
        this.tableForm.clearExtendFilter();
    }

    /**
     * Очистить критерии поиска
     */
    public clearCriteria(): void {
        this.tableForm.resetExtendFilter();
    }
}
